import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import type { SiteAnalysisResponse, SourceInfo } from '@/lib/api.types';
import type { LeadSpec } from '@/lib/report-content';
import { formatDate } from '@/lib/format';
import { ChapterLead, CheckList } from './shared';
import styles from './ChapterSummary.module.css';

type Props = {
  analysis: SiteAnalysisResponse;
};

function dedupe(items: string[]): string[] {
  return Array.from(new Set(items));
}

// 原典 links must point at the human-viewable reference page, never source.url —
// that is the raw API endpoint (tile GeoJSON / ex-api query) and renders nothing in a browser.
function hasReference(source: SourceInfo): boolean {
  return Boolean(source.reference_url && /^https?:\/\//.test(source.reference_url));
}

function sourceStatusLabel(status: SourceInfo['status']): string {
  switch (status) {
    case 'success':
      return '取得済み';
    case 'not_found':
      return '該当なし';
    case 'error':
      return 'エラー';
    default:
      return '取得スキップ';
  }
}

const LEAD: LeadSpec = {
  label: 'このレポートの読み方',
  message:
    '立地レポートは、この一枚で完結するものではありません。気になる章があれば、必ず原典・専門家・自治体の最新情報と照らし合わせてください。',
};

export type SourceRow = {
  name: string;
  provider: string | null;
  /** 「取得 YYYY年M月D日 / 取得済み」の形。 */
  fetchedLabel: string;
  referenceUrl: string | null;
};

export type SummaryContent = {
  lead: LeadSpec;
  checkTitle: string;
  allChecks: string[];
  disclaimerTitle: string;
  disclaimers: string[];
  sourcesTitle: string;
  sources: SourceRow[];
};

// まとめ章の中身。画面 (ChapterSummary) と PDF 冊子が共有する純関数。
export function buildSummaryContent(analysis: SiteAnalysisResponse): SummaryContent {
  const allChecks = dedupe([
    ...analysis.ground.check_items,
    ...analysis.hazards.check_items,
    ...analysis.planning.check_items,
    ...analysis.life_environment.check_items,
    ...analysis.price.check_items,
  ]);

  const seenSourceKeys = new Set<string>();
  const uniqueSources = analysis.sources.filter((s) => {
    const key = `${s.name}|${s.url ?? ''}`;
    if (seenSourceKeys.has(key)) return false;
    seenSourceKeys.add(key);
    return true;
  });

  return {
    lead: LEAD,
    checkTitle: '購入前に確かめたいことのまとめ',
    allChecks,
    disclaimerTitle: '注意ごと',
    disclaimers: analysis.disclaimers,
    sourcesTitle: 'このレポートに使ったデータ',
    sources: uniqueSources.map((s) => ({
      name: s.name,
      provider: s.provider ?? null,
      fetchedLabel: `取得 ${formatDate(s.fetched_at)} / ${sourceStatusLabel(s.status)}`,
      referenceUrl: hasReference(s) && s.reference_url ? s.reference_url : null,
    })),
  };
}

export default function ChapterSummary({ analysis }: Props) {
  const content = useMemo(() => buildSummaryContent(analysis), [analysis]);

  return (
    <ChapterSection id="summary">
      <ChapterLead {...content.lead} />

      <CheckList items={content.allChecks} title={content.checkTitle} />

      <div className={styles.disclaimer}>
        <h3>{content.disclaimerTitle}</h3>
        <ul>
          {content.disclaimers.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      </div>

      <div className={styles.sources}>
        <h3>{content.sourcesTitle}</h3>
        <ul>
          {content.sources.map((s, i) => (
            <li key={`${s.name}-${i}`}>
              <span className={styles.sourceName}>{s.name}</span>
              {s.provider && (
                <span className={styles.sourceProvider}>提供: {s.provider}</span>
              )}
              <span className={styles.sourceStatus}>{s.fetchedLabel}</span>
              {s.referenceUrl && (
                <a
                  className={styles.sourceLink}
                  href={s.referenceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  原典 →
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    </ChapterSection>
  );
}
